import Box from '$tablet-app/src/app/components/Box';
import { Button } from '$tablet-app/src/app/components/Button/Button';
import { DropdownMenu } from '$tablet-app/src/app/components/DropdownMenu/DropdownMenu';
import { DropDownMenuOption } from '$tablet-app/src/app/components/DropdownMenu/DropdownMenu.props';
import FieldLabel from '$tablet-app/src/app/components/Form/FieldLabel';
import PhoneNumber from '$tablet-app/src/app/components/PhoneNumber';
import { Text } from '$tablet-app/src/app/components/Text/Text';
import sendContactUsEmail from '$tablet-app/src/app/email/contactUs';
import { layout, margins, palette } from '$tablet-app/src/app/theme';
import { TextInput } from '@akin-home/forms/native';
import { OptionType } from '@akin-home/options';
import { useListOptionsByType } from '@akin-home/options/react';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { Image } from 'react-native-elements/dist/image/Image';
import styled from 'styled-components/native';
import { KeyboardAvoid } from '../../components/KeyboardAvoid';
import { useCurrentUser } from '../../users';

const { mb5 } = margins;

// Magic numbers to have the image fit just right
const imageValues = {
  height: 249,
  width: 300,
  marginTop: -73,
  containerHeight: 200,
};

const ContactUsScreen: FC = () => {
  const user = useCurrentUser();
  const { data } = useListOptionsByType({ type: OptionType.CONTACT_US_TOPIC });

  const topicOptions = useMemo(
    () =>
      data?.map(
        (opt) =>
          ({
            id: opt.id,
            title: opt.label,
          } as DropDownMenuOption)
      ),
    [data]
  );

  const [state, setState] = useState({
    topic: topicOptions?.[0],
    description: '',
    locked: false,
  });

  const onChangeDescription = (description: string) =>
    setState({ ...state, description });

  const onChangeDropdown = (topic: DropDownMenuOption) =>
    setState({ ...state, topic });

  const onSubmit = () => {
    if (!state.description) return;

    setState({
      ...state,
      locked: true,
    });

    sendContactUsEmail({
      user,
      topic: state.topic?.title || 'Unknown',
      text: state.description,
    });
  };

  useEffect(() => {
    let timeout: any;
    if (state.locked) {
      timeout = setTimeout(() => {
        setState((state) => ({ ...state, locked: false }));
      }, 1000 * 60);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [state.locked]);

  useEffect(() => {
    if (topicOptions?.length && !state.topic) {
      setState({ ...state, topic: topicOptions[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicOptions]);

  const { width } = useWindowDimensions();
  const isSmall = width < 600;
  const sizeStyles = isSmall ? smallStyles : largeStyles;

  return (
    <Container>
      <KeyboardAvoid
        style={[
          styles.keyboardAvoidContainer,
          sizeStyles.keyboardAvoidContainer,
        ]}
      >
        <View style={[styles.body, sizeStyles.body]}>
          <Box
            style={[styles.primaryBox, sizeStyles.primaryBox]}
            color={palette.Background_blue}
          >
            <View style={{ flex: 1 }}>
              <Text style={mb5} weight="semibold">
                Contact customer support
              </Text>

              <View style={{ flexWrap: 'wrap', flexDirection: 'row' }}>
                <Text>Phone Akin between </Text>
                <Text>9am - 6pm</Text>
              </View>
              <Text style={mb5}>Monday - Friday</Text>

              <PhoneNumber>1300 108 804</PhoneNumber>
            </View>

            {!isSmall ? (
              <View style={styles.imageContainer}>
                <Image
                  style={styles.image}
                  source={require('../../../../assets/Contact_us_img.png')}
                />
              </View>
            ) : null}
          </Box>

          <View>
            <FieldLabel>Email Akin support</FieldLabel>
            <DropdownMenu
              onChange={onChangeDropdown}
              value={state.topic}
              options={topicOptions || []}
            />
          </View>

          <View>
            <FieldLabel>Tell us what's happening</FieldLabel>
            <TextInput
              placeholder="Tell us the details..."
              value={state.description}
              onChangeText={onChangeDescription}
              multiline
              textInputStyle={styles.textInputStyle}
            />
          </View>

          <View style={layout.verticallyCenteredRow}>
            <Button
              text="Send feedback"
              disabled={!state.description || state.locked}
              onPress={onSubmit}
            />
            {state.locked ? (
              <>
                <FontAwesomeIcon
                  style={margins.ml6}
                  icon={['fas', 'check-circle']}
                  size={30}
                  color={palette.Bright_green}
                />
                <Text style={margins.ml6}>Thanks for your feedback!</Text>
              </>
            ) : null}
          </View>
        </View>
      </KeyboardAvoid>
    </Container>
  );
};

const Container = styled.ScrollView.attrs(() => ({
  contentContainerStyle: {
    flexGrow: 1,
    padding: 16,
  },
}))`
  flex: 1;
  background-color: white;
`;

// Styles
const styles = StyleSheet.create({
  keyboardAvoidContainer: {},
  body: {
    gap: 20,
  },
  primaryBox: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  imageContainer: {
    marginTop: imageValues.marginTop,
    flex: 1,
    alignItems: 'center',
  },
  image: {
    width: imageValues.width,
    height: imageValues.height,
  },
  textInputStyle: {
    height: 'auto',
    maxHeight: 170,
  },
});

const smallStyles = StyleSheet.create({
  keyboardAvoidContainer: {
    marginBottom: '10%',
  },
  body: {},
  primaryBox: {},
});

const largeStyles = StyleSheet.create({
  keyboardAvoidContainer: {
    marginBottom: '15%',
  },
  body: {
    marginTop: 20,
  },
  primaryBox: {
    height: imageValues.containerHeight,
  },
});

export default ContactUsScreen;
